import animalHabitats from '../content/animals/guidedLesson.animal-habitats.json';
import habitatFeatures from '../content/animals/guidedLesson.habitat-features.json';
import animalDiets from '../content/animals/guidedLesson.animal-diets.json';
import animalAdaptations from '../content/animals/guidedLesson.animal-adaptations.json';
import robotPath from '../content/robotics/guidedLesson.robot-path.json';
import robotDebug from '../content/robotics/guidedLesson.robot-debug.json';
import sensorsIntro from '../content/robotics/guidedLesson.sensors-intro.json';
import loopsIntro from '../content/robotics/guidedLesson.loops-intro.json';
import gearsAndRpm from '../content/robotics/guidedLesson.gears-and-rpm.json';
import simpleCircuits from '../content/robotics/guidedLesson.simple-circuits.json';
import gearDirectionDetective from '../content/robotics/guidedLesson.gear-direction-detective.json';
import gearSpeedRace from '../content/robotics/guidedLesson.gear-speed-race.json';
import circuitPathBuilder from '../content/robotics/guidedLesson.circuit-path-builder.json';
import switchLogicPlayground from '../content/robotics/guidedLesson.switch-logic-playground.json';
import patternMachineBasics from '../content/robotics/guidedLesson.pattern-machine-basics.json';
import causeEffectMachines from '../content/robotics/guidedLesson.cause-effect-machines.json';
import numberPatterns from '../content/math/guidedLesson.number-patterns.json';
import growingPatterns from '../content/math/guidedLesson.growing-patterns.json';
import shapeDetective from '../content/math/guidedLesson.shape-detective.json';
import timesTablesIntro from '../content/math/guidedLesson.times-tables-intro.json';
import groupsAndArrays from '../content/math/guidedLesson.groups-and-arrays.json';
import moonOrbitBasics from '../content/space/guidedLesson.moon-orbit-basics.json';
import marsRoverSignals from '../content/space/guidedLesson.mars-rover-signals.json';
import planetDetective from '../content/space/guidedLesson.planet-detective.json';
import pebblesKindness01 from '../content/pebbles/guidedLesson.pebbles-kindness-01.json';
import pebblesSharing01 from '../content/pebbles/guidedLesson.pebbles-sharing-01.json';
import pebblesPatience01 from '../content/pebbles/guidedLesson.pebbles-patience-01.json';
import pebblesTeamwork01 from '../content/pebbles/guidedLesson.pebbles-teamwork-01.json';
import { getPathLessons as graphPathLessons } from './skillGraph';

const RAW_REGISTRY = {
  'animals:animal-habitats': animalHabitats,
  'animals:habitat-features': habitatFeatures,
  'animals:animal-diets': animalDiets,
  'animals:animal-adaptations': animalAdaptations,
  'robotics:robot-path-sequencing': robotPath,
  'robotics:robot-debug': robotDebug,
  'robotics:sensors-intro': sensorsIntro,
  'robotics:loops-intro': loopsIntro,
  'robotics:gears-and-rpm': gearsAndRpm,
  'robotics:simple-circuits': simpleCircuits,
  'robotics:gear-direction-detective': gearDirectionDetective,
  'robotics:gear-speed-race': gearSpeedRace,
  'robotics:circuit-path-builder': circuitPathBuilder,
  'robotics:switch-logic-playground': switchLogicPlayground,
  'robotics:pattern-machine-basics': patternMachineBasics,
  'robotics:cause-effect-machines': causeEffectMachines,
  'math:number-patterns': numberPatterns,
  'math:growing-patterns': growingPatterns,
  'math:shape-detective': shapeDetective,
  'math:times-tables-intro': timesTablesIntro,
  'math:groups-and-arrays': groupsAndArrays,
  'space:moon-orbit-basics': moonOrbitBasics,
  'space:mars-rover-signals': marsRoverSignals,
  'space:planet-detective': planetDetective,
  'pebbles:pebbles-kindness-01': pebblesKindness01,
  'pebbles:pebbles-sharing-01': pebblesSharing01,
  'pebbles:pebbles-patience-01': pebblesPatience01,
  'pebbles:pebbles-teamwork-01': pebblesTeamwork01,
};

/**
 * @param {typeof animalHabitats} lesson
 */
export function totalEstimatedSecondsForLesson(lesson) {
  if (!lesson?.sequence?.length) return 0;
  return lesson.sequence.reduce((sum, step) => sum + (Number(step.estimatedSeconds) || 0), 0);
}

/**
 * @param {typeof animalHabitats} lesson
 */
export function estimatedMinutesFromLesson(lesson) {
  const sec = totalEstimatedSecondsForLesson(lesson);
  return Math.max(1, Math.ceil(sec / 60));
}

/**
 * @param {typeof animalHabitats} raw
 */
function finalizeLesson(raw) {
  const minutes = estimatedMinutesFromLesson(raw);
  return {
    ...raw,
    estimatedMinutes: minutes,
  };
}

const REGISTRY = Object.fromEntries(
  Object.entries(RAW_REGISTRY).map(([k, v]) => [k, finalizeLesson(v)]),
);

/**
 * @param {string} worldId
 * @param {string} pathId - same as learning path id / lessonId for guided content
 * @returns {ReturnType<finalizeLesson> | null}
 */
export function getGuidedLesson(worldId, pathId) {
  if (!worldId || !pathId) return null;
  return REGISTRY[`${worldId}:${pathId}`] ?? null;
}

export function hasGuidedLesson(worldId, pathId) {
  return !!getGuidedLesson(worldId, pathId);
}

/**
 * Look up a finalized lesson by lessonId alone (searches all worlds).
 * Used by lessonLoader so the dynamic loader does not need worldId.
 * @param {string} lessonId
 * @returns {ReturnType<typeof finalizeLesson> | null}
 */
export function findGuidedLessonById(lessonId) {
  if (!lessonId) return null;
  const id = String(lessonId).trim();
  const key = Object.keys(RAW_REGISTRY).find((k) => k.split(':')[1] === id);
  return key ? REGISTRY[key] ?? null : null;
}

/**
 * Lesson ids that have guided JSON for this world (home grid progress / completion).
 * @param {string} worldId
 * @returns {string[]}
 */
export function getGuidedLessonIdsForWorld(worldId) {
  if (!worldId) return [];
  const prefix = `${worldId}:`;
  return Object.keys(RAW_REGISTRY)
    .filter((k) => k.startsWith(prefix))
    .map((k) => k.slice(prefix.length));
}

/**
 * Return the ordered guided lessons that belong to a learning path. Honors the
 * per-world `paths.json` series when present, otherwise falls back to the
 * single guided lesson keyed by `pathId`.
 * @param {string} worldId
 * @param {string} pathId
 */
export function getPathLessons(worldId, pathId) {
  const ids = graphPathLessons(worldId, pathId);
  return ids
    .map((lessonId) => getGuidedLesson(worldId, lessonId))
    .filter(Boolean);
}

/**
 * Counts of authored vs. planned lessons in the path. Useful for surfacing
 * "1 of 3 lessons available" labels without exposing internals.
 */
export function getPathLessonCounts(worldId, pathId) {
  const ids = graphPathLessons(worldId, pathId);
  const available = ids.filter((lid) => hasGuidedLesson(worldId, lid)).length;
  return { available, planned: ids.length };
}

/** @param {unknown} activityType from step */
function normalizeActivityTypeKey(activityType) {
  if (activityType == null) return '';
  return String(activityType)
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');
}

/** @param {string} activityType from step */
export function guidedActivityTypeToAppKey(activityType) {
  const t = normalizeActivityTypeKey(activityType);
  const map = {
    match: 'engine_match',
    drag_drop: 'engine_match',
    multiple_choice: 'engine_multiple_choice',
    quiz: 'engine_multiple_choice',
    sequence: 'engine_sequence',
    sequencing: 'engine_sequence',
    pattern: 'engine_pattern',
    exploration_card: 'engine_exploration_cards',
    exploration_cards: 'engine_exploration_cards',
    canvas: 'engine_canvas_manipulative',
    canvas_manipulative: 'engine_canvas_manipulative',
    tap_on_image: 'engine_tap_on_image',
    hotspots: 'engine_tap_on_image',
    simulation: 'engine_simulation_scrub',
    simulation_scrub: 'engine_simulation_scrub',
    gear: 'engine_gear_ratio',
    gear_ratio: 'engine_gear_ratio',
    circuit: 'engine_circuit_builder',
    circuit_builder: 'engine_circuit_builder',
    gear_direction: 'engine_gear_direction',
    gear_speed: 'engine_gear_speed',
    circuit_path: 'engine_circuit_path',
    switch_logic: 'engine_switch_logic',
    system_flow: 'engine_system_flow',
    variable_simulation: 'engine_variable_simulation',
    evidence_detective: 'engine_evidence_detective',
    story_choice: 'engine_story_choice',
  };
  return map[t] || null;
}
